import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import React, { useState } from 'react';
import Modal, { ModalProps } from '@app/component/overlays/modal/Modal';
import {
	useMarketStateFilterInputTools,
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import Tag from '@app/component/tag/Tag';
import _isEmpty from 'lodash/isEmpty';
import { findProviderData } from '@app/component/provider/FindProviderData';
import DataPlatform from '@app/model/_common/DataPlatform';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import Meal from '@app/model/market_state/Meal';
import Utilities from '@util/Utilities';
import { REGION_DATA } from '@app/component/region/FindRegionData';
import Refunding from '@app/model/market_state/Refunding';
import useAppForm from '@app/site/_form';
import { z } from 'zod';
import {
	useCreateSavedMarketStateFilter
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import Message from '@app/component/message/Message';

type Props = {
	companyHotel: CompanyHotel;
} & Omit<ModalProps, 'title' | 'subtitle'>

const DashboardHeaderCreateSavedFilterModal = React.memo(
	function DashboardHeaderCreateSavedFilterModal({ companyHotel, ...props }: Props) {

		const { filter } = useMarketStateFilterInputTools();
		const toaster = useToaster();
		const {t} = useTranslation();

		const [error, setError] = useState<string | null>(null);

		const createSavedMarketStateFilter = useCreateSavedMarketStateFilter({
			onSuccess: () => {
				toaster.success('Save Custom Filter', 'Custom filter saved successfully');
				props.ref.current.close();
			},
			onError: (error: string) => {
				setError(t(error));
			}
		});

		const form = useAppForm({
			defaultValues: {
				name: '',
			},
			validators: {
				onChange: z.object({
					name: z.string().min(1, 'Name is required').max(50, 'Name must be less than 50 characters')
				}),
			},
			onSubmit: ({value}) => {
				const {competitorTagIds, ...msFilter} = filter
				return createSavedMarketStateFilter.mutateAsync({
					companyHotelId: companyHotel.id,
					name: value.name,
					filter: msFilter
				}).catch(()=>{})
			}
		});

		const reset = () => {
			form.reset();
			setError(null);
		}

		return <form.AppForm>
			<form onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}>
				<Modal title="Save Custom Filters" onClose={reset}
							 subtitle="Save your current filters as a custom filter for future use." {...props} actions={
					<form.SubmitButton state='primary'>Save</form.SubmitButton>
				}>
					<div className="wrapper mb-6">
						{error && <Message state='error' extraClassName='mb-4'>{error}</Message>}
						<p className="text-sm font-medium text-gray-000">Selected filters</p>
						<div
							className="flex flex-wrap items-center justify-start gap-2 rounded-md bg-white px-3 py-2 border border-slate-300">
							<Tag
								state="slate">{_isEmpty(filter.roomTypes) ? 'Any Room' : filter.roomTypes.map((rt: HotelRoomMappingType) => companyHotel.findRoomMappingTypeName((rt))).join(', ')}</Tag>
							<Tag state="slate">{!!filter.provider ? findProviderData(filter.provider).name : 'Any Provider'}</Tag>
							<Tag
								state="slate">{!!filter.platform ? filter.platform == DataPlatform.WEB ? 'Desktop' : 'Mobile' : 'Any Platform'}</Tag>
							<Tag state="slate">{!!filter.region ? REGION_DATA[filter.region].name : 'Any Region'}</Tag>
							<Tag state="slate">{filter.guests} Guests</Tag>
							<Tag state="slate">{filter.nights} Nights</Tag>
							<Tag
								state="slate">{(!filter.meal || filter.meal === Meal.NONE) ? 'No Meal' : Utilities.snakeCaseToWords(String(filter.meal))}</Tag>
							<Tag
								state="slate">{(!filter.refunding || filter.refunding === Refunding.NONE) ? 'No Refunding' : 'Full Refunding'}</Tag>
							<Tag state="slate">{!filter.airportCode ? 'Hotel Only' : filter.airportCode + ' Airport'}</Tag>
						</div>
					</div>
					<form.AppField name='name'>
						{(field) => <field.TextField label='Name' placeholder='Enter filter name' maxLength={20}/>}
					</form.AppField>
					<Message state='recommendation' extraClassName='mt-6'>Please think of using a short name for your custom filter. It will be much easier to use later in the app.</Message>
				</Modal>
			</form>
		</form.AppForm>;
	},
);

export default DashboardHeaderCreateSavedFilterModal;
